// --- NEW FILE: packages/agent-kit/src/hooks/tasks/useAutoSaveTask.ts ---

import { useEffect } from 'react';
import { useSWRConfig } from 'swr';
import { useDebouncedCallback } from 'use-debounce';

import type { ProjectTask, TaskPayload } from '@dopomogai/types';
import { useManageTask } from './useManageTask';
import { useUiStore } from '../../../../../apps/website/src/stores/ui'; // Adjust path

/**
 * A hook that automatically saves changes to a task after the user stops editing.
 * It reads the change queue from the global UI store and handles the API calls.
 *
 * @returns An object containing the current loading state and any error from the last save attempt.
 */
export const useAutoSaveTask = () => {
  const { mutate } = useSWRConfig();
  const { updateTask, isLoading, error } = useManageTask();
  
  // Get state and actions from the Zustand store
  const { taskInspector, setSaveStatus, clearChanges } = useUiStore();
  const { editingTask, changes, isNew } = taskInspector;

  const debouncedSave = useDebouncedCallback(async (taskToUpdate: ProjectTask, changesToSave: Partial<ProjectTask>) => {
    setSaveStatus('saving');

    // Construct the payload ONLY with the fields that have changed.
    const payload: Partial<TaskPayload> = {};
    if ('title' in changesToSave || 'description' in changesToSave) {
        payload.translations = { 'en': { title: changesToSave.title, description: changesToSave.description }};
    }
    if ('status' in changesToSave) payload.status = changesToSave.status;
    if ('task_type' in changesToSave) payload.task_type = changesToSave.task_type;
    if ('points' in changesToSave) payload.points = changesToSave.points;
    if ('links' in changesToSave) payload.links = changesToSave.links;
    if ('assignee_ids' in changesToSave) payload.assignee_ids = changesToSave.assignee_ids;

    const result = await updateTask(taskToUpdate._id, payload as TaskPayload);
    
    if (result) {
        setSaveStatus('success');
        clearChanges();
        // Revalidate sprint board data as task points/status may affect it
        if (result.sprint_id) {
            mutate(['/v1/sprint/dashboard', result.sprint_id, 'en']);
        }
    } else {
        setSaveStatus('error');
    }
  }, 1500); // 1.5-second debounce delay

  useEffect(() => {
    // Do not run if it's a new task, if there are no changes, or no task is being edited.
    if (isNew || Object.keys(changes).length === 0 || !editingTask) {
      return;
    }
    debouncedSave(editingTask, changes);

    // Cleanup function to cancel the debounce if the component unmounts
    return () => debouncedSave.cancel();
  }, [changes, editingTask, isNew, debouncedSave]);

  return { isLoading, error };
};